/**
 * Per-medicine stock resolution for the dispense screens.
 *
 * Lifted verbatim out of `pages/pharmacy/StartDispensePage.js` so the OPD flow can
 * reuse it. Every function here is pure or takes plain arguments — no React state —
 * which is what makes it shareable.
 */
import { searchPrescribeAvailability } from '../api/pharmacy';
import { getItem } from '../api/inventoryMaster';
import { resolveMedicineUomId } from './pharmacyMappers';
import { isControlledOrNarcotic } from './controlledDrug';

export const STOCK_STATUS = {
  NOT_LISTED: 'NOT_LISTED',
  OUT_OF_STOCK: 'OUT_OF_STOCK',
  IN_STOCK: 'IN_STOCK',
};

export function hasMedicineStock(item) {
  if (!item?.item_id) return false;
  if (item.has_stock === false) return false;
  const qty = Number(item.available_quantity ?? item.stock) || 0;
  return qty > 0;
}

/** Only pass dosage_form when it looks like a form (Tablet, Syrup), not a full medicine name. */
export function normalizeDosageForm(value) {
  const trimmed = String(value || '').trim();
  if (!trimmed || trimmed.length > 30 || /\d\s*(mg|ml|g|mcg)\b/i.test(trimmed)) return '';
  return trimmed;
}

export async function fetchPrescribeSearch(params) {
  const result = await searchPrescribeAvailability(params).catch(() => ({ items: [] }));
  return result.items || [];
}

export function findBrandCatalogueMatch(items, medicineName) {
  if (!items.length || !medicineName) return null;
  const normalized = String(medicineName).toLowerCase().trim();
  return items.find((item) => {
    const name = String(item.item_name || item.name || '').toLowerCase().trim();
    const brand = String(item.brand_name || '').toLowerCase().trim();
    return name === normalized || brand === normalized
      || name.includes(normalized) || normalized.includes(name);
  }) || null;
}

export async function enrichItemUom(item) {
  if (!item?.item_id) return item;
  try {
    const master = await getItem(item.item_id);
    const uomFromMaster = item.uom_id || resolveMedicineUomId(master, master?.base_uom);
    return {
      ...item,
      ...(uomFromMaster ? { uom_id: uomFromMaster, base_uom_id: uomFromMaster } : {}),
      is_controlled_drug: master?.is_controlled_drug ?? item.is_controlled_drug,
      is_narcotic: master?.is_narcotic ?? item.is_narcotic,
      controlled: isControlledOrNarcotic(master) || isControlledOrNarcotic(item),
    };
  } catch {
    // submit validation will surface missing UOM
  }
  return item;
}

/**
 * Brand-name search only — generic substitute search is manual via the Substitute button.
 * `line` needs `{ id, medicine, route? }`; OPD callers pass the free-text prescribed name
 * as `medicine` and the ABDM `route` (which `normalizeDosageForm` discards unless it
 * really is a dosage form).
 */
export async function resolvePrescribeLineStock({ line, tenantId, hospitalId, storeId }) {
  const baseParams = {
    tenant_id: tenantId,
    hospital_id: hospitalId,
    location_id: storeId,
  };
  const dosageForm = normalizeDosageForm(line.route);
  const searchOpts = dosageForm ? { dosage_form: dosageForm } : {};

  const brandItems = await fetchPrescribeSearch({
    ...baseParams,
    q: line.medicine,
    ...searchOpts,
  });
  const brandMatch = findBrandCatalogueMatch(brandItems, line.medicine);

  if (brandMatch && hasMedicineStock(brandMatch)) {
    const enriched = await enrichItemUom(brandMatch);
    return {
      lineId: line.id,
      status: STOCK_STATUS.IN_STOCK,
      item: enriched,
      message: null,
    };
  }

  if (brandMatch) {
    const enrichedPrimary = await enrichItemUom(brandMatch);
    return {
      lineId: line.id,
      status: STOCK_STATUS.OUT_OF_STOCK,
      item: enrichedPrimary,
      message: `"${brandMatch.item_name || line.medicine}" is out of stock at this store. Click Substitute to search by generic name.`,
    };
  }

  return {
    lineId: line.id,
    status: STOCK_STATUS.NOT_LISTED,
    item: null,
    message: `"${line.medicine}" is not listed in the pharmacy catalogue. Click Substitute to search by generic name.`,
  };
}

export function applyMatchedItemToLine(row, match) {
  if (!match) return row;
  return {
    ...row,
    item_id: match.item_id || row.item_id,
    uom_id: match.uom_id || match.base_uom_id || row.uom_id || '',
    unit_rate: row.unit_rate || match.rate || match.mrp || '',
    batch_number: row.batch_number || (match.batch_number || ''),
    is_controlled_drug: match.is_controlled_drug ?? row.is_controlled_drug,
    is_narcotic: match.is_narcotic ?? row.is_narcotic,
    controlled: isControlledOrNarcotic(match) || isControlledOrNarcotic(row),
  };
}
